<template>
  <div class="questionnaire-form-page questionnaire-theme">
    <van-nav-bar :title="navTitle">
      <template #right>
        <span
          v-if="formConfig && formConfig.instructions"
          class="nav-instructions-link"
          @click="showInstructionsPopup = true"
        >填写须知</span>
      </template>
    </van-nav-bar>
    <van-loading v-if="loading && !formConfig" class="loading-center" />
    <template v-else>
      <DynamicQuestionnaireForm
        ref="dynamicForm"
        :form-config="formConfig"
        :value="content"
        :readonly="false"
      />
      <div class="footer-actions">
        <van-button
          plain
          type="default"
          round
          class="btn-draft"
          :loading="saving"
          @click="saveDraft"
        >
          保存草稿
        </van-button>
        <van-button
          type="primary"
          round
          class="btn-submit"
          :loading="submitting"
          @click="submitForm"
        >
          {{ submitButtonText }}
        </van-button>
      </div>
    </template>
    <van-popup
      v-model="showInstructionsPopup"
      position="bottom"
      round
      :style="{ maxHeight: '70%' }"
    >
      <div class="instructions-popup">
        <div class="instructions-popup-title">填写须知</div>
        <div class="instructions-popup-content">{{ instructionsText }}</div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import Vue from 'vue'
import { NavBar, Button, Loading, Toast, Popup } from 'vant'
import { setTitle } from '@/libs/ds-bridge'
import { getFormDetail, getSubmissionDetail, submitSubmission } from '@/api/questionnaire'
import DynamicQuestionnaireForm from './components/DynamicQuestionnaireForm'

Vue.use(NavBar).use(Button).use(Loading).use(Toast).use(Popup)

export default {
  name: 'QuestionnaireForm',
  components: { DynamicQuestionnaireForm },
  data() {
    return {
      formId: '',
      submissionId: '',
      form: {},
      formConfig: null,
      content: {},
      loading: false,
      saving: false,
      submitting: false,
      showInstructionsPopup: false
    }
  },
  computed: {
    instructionsText() {
      return (this.formConfig && this.formConfig.instructions) || '暂无填写须知'
    },
    navTitle() {
      const st = this.form && this.form.subTitle != null ? String(this.form.subTitle).trim() : ''
      if (st) return st
      return '表单详情'
    },
    formTypeLabel() {
      return this.form.type || '问卷'
    },
    submitButtonText() {
      return `提交${this.formTypeLabel}`
    }
  },
  watch: {
    '$route.query': {
      handler(query) {
        this.formId = query.formId || ''
        this.submissionId = query.submissionId || ''
        if (this.formId) {
          this.loadForm()
          if (this.submissionId) {
            this.loadDraft()
          } else {
            this.content = {}
          }
        }
      },
      immediate: true
    },
    navTitle: 'syncEmbeddedTitle'
  },
  methods: {
    syncEmbeddedTitle() {
      const t = this.navTitle
      document.title = t
      setTitle(t)
    },
    loadForm() {
      if (!this.formId) return
      this.loading = true
      getFormDetail(this.formId)
        .then(res => {
          const data = res && (res.data !== undefined ? res.data : res)
          this.form = data || {}
          this.formConfig = (this.form.formConfig && typeof this.form.formConfig === 'object')
            ? this.form.formConfig
            : null
        })
        .finally(() => {
          this.loading = false
          this.syncEmbeddedTitle()
        })
    },
    loadDraft() {
      if (!this.formId || !this.submissionId) return
      this.loading = true
      getSubmissionDetail(this.formId, this.submissionId)
        .then(res => {
          const data = res && (res.data !== undefined ? res.data : res)
          const c = data && data.content
          this.content = (c && typeof c === 'object') ? { ...c } : {}
        })
        .finally(() => {
          this.loading = false
        })
    },
    validateRequired() {
      if (!this.formConfig || !Array.isArray(this.formConfig.fields)) return true
      const currentContent = this.getCurrentContent()
      const required = this.formConfig.fields.filter(f => f.required)
      for (const f of required) {
        const val = currentContent[f.key]
        if (val == null || val === '') {
          Toast(f.label ? `请填写${f.label}` : '请完善必填项')
          return false
        }
        if (Array.isArray(val) && val.length === 0) {
          Toast(f.label ? `请上传${f.label}` : '请完善必填项')
          return false
        }
        if (f.type === 'choice' && (f.mode === 'single' || f.mode === 'multi')) {
          const selected = (val && val.selected) || []
          if (!selected.length) {
            Toast(f.label ? `请选择${f.label}` : '请完善必填项')
            return false
          }
          const opts = f.options || []
          for (const opt of opts) {
            if (opt.allowInput && opt.inputRequired && selected.includes(opt.value)) {
              const extra = (val && val.extraInputMap || {})[opt.value]
              if (!extra || String(extra).trim() === '') {
                Toast(opt.label ? `请填写${opt.label}的补充内容` : '请完善必填项')
                return false
              }
            }
          }
        }
      }
      return true
    },
    getCurrentContent() {
      const ref = this.$refs.dynamicForm
      return (ref && typeof ref.getContent === 'function') ? ref.getContent() : this.content
    },
    doSubmit(submitStatus) {
      const payload = {
        submitStatus,
        content: this.getCurrentContent()
      }
      if (this.submissionId) {
        payload.submissionId = this.submissionId
      }
      return submitSubmission(this.formId, payload)
    },
    saveDraft() {
      this.saving = true
      this.doSubmit('DRAFT')
        .then(res => {
          const data = res && (res.data !== undefined ? res.data : res)
          const id = data && data.id
          if (id && !this.submissionId) {
            this.submissionId = String(id)
            this.$router.replace({
              path: '/questionnaire/form',
              query: { formId: this.formId, submissionId: this.submissionId }
            }).catch(() => {})
          }
          Toast('草稿已保存')
        })
        .catch(() => {
          Toast('保存失败')
        })
        .finally(() => {
          this.saving = false
        })
    },
    submitForm() {
      if (!this.validateRequired()) return
      this.submitting = true
      this.doSubmit('SUBMITTED')
        .then(() => {
          Toast('提交成功')
          this.$router.replace({
            path: '/questionnaire/detail',
            query: { formId: this.formId, tab: 'myList' }
          }).catch(() => {})
        })
        .catch(() => {
          Toast('提交失败')
        })
        .finally(() => {
          this.submitting = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.questionnaire-form-page {
  min-height: 100vh;
  padding-bottom: 100px;
  background: #f5f5f5;
}

.nav-instructions-link {
  font-size: 12px;
  color: var(--qn-theme);
}

.loading-center {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.instructions-popup {
  padding: 20px 16px 32px;

  .instructions-popup-title {
    font-size: 16px;
    font-weight: 600;
    color: #323233;
    margin-bottom: 12px;
  }

  .instructions-popup-content {
    font-size: 14px;
    line-height: 1.6;
    color: #646566;
    white-space: pre-wrap;
    max-height: 50vh;
    overflow-y: auto;
  }
}

.footer-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: #fff;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  gap: 12px;

  .btn-draft {
    flex: 1 1 0;
    min-width: 0;
    height: 44px;
  }

  .btn-submit {
    flex: 2 1 0;
    min-width: 0;
    height: 44px;
    font-size: 16px;
    background: var(--qn-theme);
    border: none;
  }
}
</style>
